import { Field, FieldInput, FieldLabel, FieldTextarea } from '@ibe/ui';
import { type ReactNode } from 'react';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';

import { type RoleFormInput } from '../api/schemas.js';
import { PermissionTree } from './permission-tree.js';

/**
 * A role's inputs, independent of what surrounds them.
 *
 * Separated from the pages because create and edit are the same fields in two
 * containers, and a field set that lives inside one of them gets copied the
 * moment the other appears — after which they drift.
 */

export interface RoleFieldsProps {
  readonly register: UseFormRegister<RoleFormInput>;
  readonly errors: FieldErrors<RoleFormInput>;
  readonly control: Control<RoleFormInput>;
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

export function RoleFields({ register, errors, control }: RoleFieldsProps): React.JSX.Element {
  return (
    <div className="space-y-6">
      <Section
        title="Identity"
        description="A name the staff team will recognise, and a short description of what the role is for."
      >
        <Field error={errors.name?.message} required>
          <FieldLabel>Role name</FieldLabel>
          <FieldInput autoComplete="off" placeholder="Front desk manager" {...register('name')} />
        </Field>

        <Field
          error={errors.description?.message}
          description="What this role is for — optional, but helpful once there are more than a few."
        >
          <FieldLabel>Description</FieldLabel>
          <FieldTextarea rows={3} {...register('description')} />
        </Field>
      </Section>

      <Section
        title="Permissions"
        description="Only the permissions you hold yourself can be granted to this role. If a permission is missing, the tenant owner or a platform administrator must hold it first."
      >
        <Controller
          control={control}
          name="permissions"
          render={({ field }) => (
            <PermissionTree
              selected={field.value}
              onChange={(permissions) => {
                field.onChange(permissions);
              }}
              error={errors.permissions?.message}
            />
          )}
        />
      </Section>
    </div>
  );
}
